/* iter322 FE — Manual CAPA + AI Draft Modal */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { Modal, Btn, Input, Select, Textarea } from "@/components/UI";
import { Loader2, Sparkles } from "lucide-react";

const EMPTY = {
  title: "", description: "",
  problem_definition: "", root_cause: "",
  corrective_action: "", preventive_action: "",
  effectiveness_check: "",
  assigned_to: "", department: "Kalite / GMP",
  priority: "high", due_date: "", notes: "",
};

export default function ManualCapaModal({ open, onClose, onCreated }) {
  const [doc, setDoc] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState(null);

  const close = () => { setDoc(EMPTY); setAiMsg(null); onClose?.(); };

  const save = async () => {
    if (!doc.title.trim() || !doc.description.trim()) {
      alert("Başlık ve açıklama zorunlu"); return;
    }
    setBusy(true);
    try {
      await api.post("/gmp/manual-capa", {
        title: doc.title.trim(),
        description: doc.description.trim(),
        problem_definition: doc.problem_definition || null,
        root_cause: doc.root_cause || null,
        corrective_action: doc.corrective_action || null,
        preventive_action: doc.preventive_action || null,
        effectiveness_check: doc.effectiveness_check || null,
        assigned_to: doc.assigned_to || null,
        department: doc.department,
        priority: doc.priority,
        due_date: doc.due_date || null,
        notes: doc.notes || null,
      });
      onCreated?.(); close();
    } catch (e) {
      alert(e.response?.data?.detail || "Kaydedilemedi");
    } finally { setBusy(false); }
  };

  const runAi = async () => {
    const topic = doc.description.trim() || doc.title.trim();
    if (topic.length < 10) {
      alert("AI taslağı için en az 10 karakterlik bir açıklama girin"); return;
    }
    setAiBusy(true); setAiMsg(null);
    try {
      const r = await api.post("/gmp/manual-capa/ai-draft", {
        issue_topic: topic,
        existing_fields: {
          problem_definition: doc.problem_definition,
          root_cause: doc.root_cause,
          corrective_action: doc.corrective_action,
          preventive_action: doc.preventive_action,
          effectiveness_check: doc.effectiveness_check,
        },
      });
      if (r.ai_status === "suggested") {
        const draft = r.draft || {};
        setDoc(d => ({
          ...d,
          problem_definition: draft.problem_definition ?? d.problem_definition,
          root_cause: draft.root_cause ?? d.root_cause,
          corrective_action: draft.corrective_action ?? d.corrective_action,
          preventive_action: draft.preventive_action ?? d.preventive_action,
          effectiveness_check: draft.effectiveness_check ?? d.effectiveness_check,
        }));
        setAiMsg(`AI taslak uygulandı · ${Object.keys(draft).length} alan dolduruldu${r.skipped_user_filled_fields?.length ? ` · ${r.skipped_user_filled_fields.length} alan korundu (dolu)` : ""}`);
      } else {
        setAiMsg(`AI başarısız: ${r.error || "bilinmeyen"}`);
      }
    } catch (e) {
      setAiMsg(e.response?.data?.detail || "AI taslak başarısız");
    } finally { setAiBusy(false); }
  };

  return (
    <Modal open={open} onClose={close} title="+ Manuel CAPA Oluştur" size="xl">
      <div className="space-y-3" data-testid="manual-capa-modal">
        <div className="bg-fuchsia-50 border border-fuchsia-200 rounded p-2 text-[11px] text-fuchsia-900">
          Mevcut kaynaklara bağlı olmayan manuel CAPA kaydı. Sistem tarafından otomatik üretilen CAPA'larla aynı listede görünür.
          <b> AI opsiyoneldir</b> — otomatik çağrılmaz, kaydetmez, onaylamaz.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Input label="Başlık *" value={doc.title}
                    onChange={e => setDoc({ ...doc, title: e.target.value })}
                    data-testid="mcapa-title"/>
          </div>
          <div className="col-span-2">
            <Textarea label="Konu / Olay Açıklaması *" rows={2} value={doc.description}
                       onChange={e => setDoc({ ...doc, description: e.target.value })}
                       data-testid="mcapa-desc"
                       placeholder="Ör. Üretim alanında dolum sonrası kapaklarda gevşeklik tespit edildi"/>
          </div>

          <div className="col-span-2 flex items-center gap-2 bg-fuchsia-50/60 border border-fuchsia-200 rounded p-2">
            <Btn variant="secondary" size="sm" onClick={runAi} disabled={aiBusy}
                  data-testid="mcapa-ai-draft">
              {aiBusy ? <Loader2 size={11} className="animate-spin"/> : <Sparkles size={11}/>}
              {aiBusy ? "AI çalışıyor..." : "AI ile Taslak Oluştur"}
            </Btn>
            <span className="text-[11px] text-fuchsia-900 flex-1">{aiMsg || "AI yalnız boş alanları doldurur. Mevcut değerler korunur."}</span>
          </div>

          <div className="col-span-2"><Textarea label="Problem Tanımı" rows={2} value={doc.problem_definition}
                       onChange={e => setDoc({ ...doc, problem_definition: e.target.value })} data-testid="mcapa-problem"/></div>
          <div className="col-span-2"><Textarea label="Kök Neden" rows={2} value={doc.root_cause}
                       onChange={e => setDoc({ ...doc, root_cause: e.target.value })} data-testid="mcapa-root"/></div>
          <Textarea label="Düzeltici Faaliyet" rows={2} value={doc.corrective_action}
                     onChange={e => setDoc({ ...doc, corrective_action: e.target.value })} data-testid="mcapa-corr"/>
          <Textarea label="Önleyici Faaliyet" rows={2} value={doc.preventive_action}
                     onChange={e => setDoc({ ...doc, preventive_action: e.target.value })} data-testid="mcapa-prev"/>
          <div className="col-span-2"><Textarea label="Etkinlik Kontrol" rows={2} value={doc.effectiveness_check}
                       onChange={e => setDoc({ ...doc, effectiveness_check: e.target.value })} data-testid="mcapa-eff"/></div>

          <Input label="Sorumlu" value={doc.assigned_to}
                  onChange={e => setDoc({ ...doc, assigned_to: e.target.value })} data-testid="mcapa-assigned"/>
          <Input label="Hedef Tarih" type="date" value={doc.due_date}
                  onChange={e => setDoc({ ...doc, due_date: e.target.value })}/>
          <Select label="Öncelik" value={doc.priority}
                   onChange={e => setDoc({ ...doc, priority: e.target.value })} data-testid="mcapa-priority">
            <option value="low">Düşük</option>
            <option value="normal">Normal</option>
            <option value="high">Yüksek</option>
            <option value="critical">Kritik</option>
          </Select>
          <Input label="Departman" value={doc.department}
                  onChange={e => setDoc({ ...doc, department: e.target.value })}/>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Btn variant="secondary" onClick={close}>Vazgeç</Btn>
          <Btn onClick={save} disabled={busy || !doc.title.trim() || !doc.description.trim()} data-testid="mcapa-save">
            {busy ? <Loader2 size={11} className="animate-spin"/> : null} CAPA Kaydet
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
